import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Search,
  RefreshCw,
  Shield,
  KeyRound,
  LogOut,
  Trash2,
  RotateCcw,
  ScrollText,
  Loader2,
  ChevronLeft,
} from 'lucide-react';
import { authService } from '@/services/auth';
import type { User, AuditLogEntry } from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/layout/PageLayout';
import { toast } from 'sonner';

type StatusFilter = 'all' | 'active' | 'inactive' | 'deleted';
type RoleFilter = 'all' | User['role'];

function formatWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function roleBadge(role: User['role']) {
  const colors =
    role === 'admin'
      ? 'bg-violet-500/20 text-violet-300'
      : role === 'editor'
        ? 'bg-blue-500/20 text-blue-300'
        : 'bg-khiimori-bg-elevated/20 text-khiimori-text-muted';
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${colors}`}>{role}</span>
  );
}

export default function AdminUsersPage() {
  const currentUser = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<User[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [userRows, logs] = await Promise.all([
        authService.listUsers({
          q: search.trim() || undefined,
          role: roleFilter === 'all' ? undefined : roleFilter,
          status: statusFilter,
        }),
        authService.listAdminAuditLogs(30),
      ]);
      setUsers(userRows);
      setAuditLogs(logs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const active = users.filter((u) => (u.is_active ?? true) && !u.deleted_at).length;
    const admins = users.filter((u) => u.role === 'admin' && !u.deleted_at).length;
    return { total: users.length, active, admins };
  }, [users]);

  const runAction = async (userId: string, action: () => Promise<void>, success: string) => {
    setBusyUserId(userId);
    try {
      await action();
      toast.success(success);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyUserId(null);
    }
  };

  const refreshAudit = async () => {
    setAuditLoading(true);
    try {
      setAuditLogs(await authService.listAdminAuditLogs(30));
    } catch {
      toast.error('Failed to refresh audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <PageLayout
      title="User Management"
      description="Manage users, roles, access, and review admin audit trail"
      icon={Users}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ChevronLeft className="mr-1 h-4 w-4" />
              Settings
            </Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-4">
          <div className="text-2xl font-bold text-khiimori-text-primary">{stats.total}</div>
          <div className="text-sm text-khiimori-text-muted">Users shown</div>
        </div>
        <div className="rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-4">
          <div className="text-2xl font-bold text-emerald-400">{stats.active}</div>
          <div className="text-sm text-khiimori-text-muted">Active</div>
        </div>
        <div className="rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-4">
          <div className="text-2xl font-bold text-violet-400">{stats.admins}</div>
          <div className="text-sm text-khiimori-text-muted">Admins</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, username, or name…"
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
            className="rounded-lg border border-khiimori-border/10 bg-khiimori-bg-elevated/5 px-3 py-2 text-sm text-khiimori-text-primary"
            aria-label="Filter by role"
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-lg border border-khiimori-border/10 bg-khiimori-bg-elevated/5 px-3 py-2 text-sm text-khiimori-text-primary"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>

      {error ? <p className="mb-4 text-sm text-red-400">{error}</p> : null}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-violet-400">
          <Loader2 className="mr-2 h-6 w-6 animate-spin" />
          Loading users…
        </div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center text-khiimori-text-muted">
          <Users className="mx-auto mb-3 h-10 w-10 opacity-50" />
          <p className="text-sm">No users match your filters</p>
        </div>
      ) : (
        <div className="overflow-auto rounded-xl border border-khiimori-border/10">
          <table className="min-w-full text-sm">
            <thead className="bg-khiimori-bg-elevated/80 text-left text-khiimori-text-muted">
              <tr>
                <th className="p-3 font-medium">User</th>
                <th className="p-3 font-medium">Role</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Last active</th>
                <th className="p-3 font-medium">Sessions</th>
                <th className="p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isDeleted = Boolean(user.deleted_at);
                const isActive = (user.is_active ?? true) && !isDeleted;
                const isSelf = user.id === currentUser?.id;
                const busy = busyUserId === user.id;

                return (
                  <tr key={user.id} className="border-t border-khiimori-border/10">
                    <td className="p-3">
                      <div className="font-medium text-khiimori-text-primary">
                        {user.full_name || user.name || user.username}
                        {isSelf ? (
                          <span className="ml-2 text-xs text-violet-400">(you)</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-khiimori-text-muted">{user.email}</div>
                    </td>
                    <td className="p-3">
                      {isDeleted ? (
                        roleBadge(user.role)
                      ) : (
                        <select
                          value={user.role}
                          disabled={busy}
                          onChange={(e) =>
                            void runAction(
                              user.id,
                              () => authService.updateUserRole(user.id, e.target.value as User['role']),
                              'Role updated',
                            )
                          }
                          className="rounded border border-khiimori-border/10 bg-khiimori-bg-elevated/5 px-2 py-1 text-sm"
                          aria-label={`Role for ${user.email}`}
                        >
                          <option value="viewer">viewer</option>
                          <option value="editor">editor</option>
                          <option value="admin">admin</option>
                        </select>
                      )}
                    </td>
                    <td className="p-3">
                      {isDeleted ? (
                        <span className="rounded bg-red-500/20 px-2 py-0.5 text-xs text-red-300">Deleted</span>
                      ) : isActive ? (
                        <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">Active</span>
                      ) : (
                        <span className="rounded bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300">Inactive</span>
                      )}
                    </td>
                    <td className="p-3 text-khiimori-text-muted">{formatWhen(user.last_login_at)}</td>
                    <td className="p-3 text-khiimori-text-muted">{user.session_count ?? 0}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {!isDeleted ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              title={isActive ? 'Deactivate' : 'Activate'}
                              onClick={() =>
                                void runAction(
                                  user.id,
                                  () => authService.updateUserStatus(user.id, !isActive),
                                  isActive ? 'User deactivated' : 'User activated',
                                )
                              }
                            >
                              <Shield className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              title="Send password reset"
                              onClick={() =>
                                void runAction(user.id, async () => {
                                  const res = await authService.adminResetUserPassword(user.id);
                                  if (res.dev_reset_url) {
                                    toast.message('Dev reset link', { description: res.dev_reset_url });
                                  }
                                }, 'Password reset sent')
                              }
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              title="Revoke all sessions"
                              onClick={() =>
                                void runAction(
                                  user.id,
                                  () => authService.adminRevokeUserSessions(user.id),
                                  'Sessions revoked',
                                )
                              }
                            >
                              <LogOut className="h-4 w-4" />
                            </Button>
                            {!isSelf ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                title="Soft delete (30-day recovery)"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => {
                                  if (!window.confirm(`Delete ${user.email}? Recoverable for 30 days.`)) return;
                                  void runAction(
                                    user.id,
                                    () => authService.adminDeleteUser(user.id),
                                    'User deleted',
                                  );
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            title="Restore user"
                            onClick={() =>
                              void runAction(
                                user.id,
                                () => authService.adminRestoreUser(user.id),
                                'User restored',
                              )
                            }
                          >
                            <RotateCcw className="mr-1 h-4 w-4" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-5">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-violet-400" />
            <h2 className="font-semibold text-khiimori-text-primary">Audit trail</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => void refreshAudit()} disabled={auditLoading}>
            <RefreshCw className={`h-4 w-4 ${auditLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {auditLogs.length === 0 ? (
          <p className="text-sm text-khiimori-text-muted">No admin actions logged yet</p>
        ) : (
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {auditLogs.map((log) => (
              <div
                key={log.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-khiimori-bg-elevated/30 px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-medium text-khiimori-text-primary">{log.action}</span>
                  {log.resource_type ? (
                    <span className="ml-2 text-khiimori-text-muted">
                      {log.resource_type}
                      {log.resource_id ? ` · ${log.resource_id.slice(0, 8)}…` : ''}
                    </span>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-khiimori-text-muted">{formatWhen(log.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
